import React, { useState, useEffect, useCallback } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Send, Play, Pause, RefreshCw, MessageSquare, Voicemail, Archive, Search, Download, Calendar } from 'lucide-react';
import { neonRawSelect, neonQuery } from '../services/api';
import VipBadge from '../components/VipBadge';
import { getCurrentUser } from '../services/auth';

const RC_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/ringcentral-proxy'
  : '/.netlify/functions/ringcentral-proxy';

const CX_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/cxone-proxy'
  : '/.netlify/functions/cxone-proxy';

// Known company numbers — show friendly label instead of customer lookup
const KNOWN_NUMBERS = {
  '4109683167': 'CP Local',
  '8773282722': 'CP TollFree',
};

function formatPhone(num) {
  if (!num) return '';
  const digits = String(num).replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return num;
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function directionBadge(direction, result) {
  const missed = result === 'Missed' || result === 'Voicemail' || result === 'Abandoned';
  const outbound = direction === 'Outbound' || direction === true;
  if (missed) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#c0392b', fontWeight: 600, fontSize: 11 }}><PhoneMissed size={13} /> Missed</span>;
  if (outbound) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#2563eb', fontWeight: 600, fontSize: 11 }}><PhoneOutgoing size={13} /> Outbound</span>;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1a8a4a', fontWeight: 600, fontSize: 11 }}><PhoneIncoming size={13} /> Inbound</span>;
}

async function rcCall(action, params = {}) {
  const res = await fetch(RC_PROXY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, params }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `RC API error ${res.status}`);
  return data;
}

async function cxCall(action, params = {}) {
  const res = await fetch(CX_PROXY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, params }) });
  const data = await res.json();
  if (!res.ok && !data.completedContacts) throw new Error(data.error || `CXone API error ${res.status}`);
  return data;
}

/* ── Recording Player ── */
function RecordingPlayer({ url }) {
  const [state, setState] = useState('idle'); // idle | loading | ready | playing | error
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = React.useRef(null);
  const blobUrlRef = React.useRef(null);

  const loadAudio = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const res = await fetch(
        window.location.hostname === 'localhost'
          ? 'http://localhost:8888/.netlify/functions/cxone-proxy'
          : '/.netlify/functions/cxone-proxy',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'recording-stream', params: { url } }),
        }
      );
      if (!res.ok) throw new Error('Failed to fetch recording');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      blobUrlRef.current = blobUrl;
      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
        setState('ready');
        audio.play();
        setState('playing');
      });
      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime);
        setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
      });
      audio.addEventListener('ended', () => setState('ready'));
      audio.addEventListener('error', () => setState('error'));
      audio.load();
    } catch {
      setState('error');
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return loadAudio();
    if (state === 'playing') {
      audioRef.current.pause();
      setState('ready');
    } else {
      audioRef.current.play();
      setState('playing');
    }
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * duration;
  };

  // Cleanup blob URL on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const fmtTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 180 }}>
      <button
        onClick={togglePlay}
        disabled={state === 'loading'}
        style={{
          background: state === 'playing' ? 'var(--orange)' : 'var(--navy)',
          border: 'none',
          borderRadius: '50%',
          width: 24, height: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: state === 'loading' ? 'wait' : 'pointer',
          flexShrink: 0,
          opacity: state === 'loading' ? 0.5 : 1,
        }}
      >
        {state === 'loading' ? (
          <RefreshCw size={11} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
        ) : state === 'playing' ? (
          <Pause size={11} color="#fff" />
        ) : (
          <Play size={11} color="#fff" style={{ marginLeft: 1 }} />
        )}
      </button>
      {(state === 'ready' || state === 'playing') && duration > 0 ? (
        <>
          <div
            onClick={seek}
            style={{
              flex: 1,
              height: 6,
              background: 'var(--gray-200)',
              borderRadius: 3,
              cursor: 'pointer',
              position: 'relative',
              minWidth: 80,
            }}
          >
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: state === 'playing' ? 'var(--orange)' : 'var(--navy)',
              borderRadius: 3,
              transition: 'width 0.1s linear',
            }} />
          </div>
          <span style={{ fontSize: 9, color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {fmtTime(currentTime)}/{fmtTime(duration)}
          </span>
        </>
      ) : state === 'error' ? (
        <span style={{ fontSize: 10, color: 'var(--red)' }}>Error</span>
      ) : state === 'idle' ? (
        <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>Play</span>
      ) : null}
    </div>
  );
}

/* ── Call Log Tab (CXone Contact Center) ── */
function CallLogTab({ navigateTo }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preset, setPreset] = useState('today'); // 'today' | 'yesterday' | '3d' | '7d' | 'custom'
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customerMap, setCustomerMap] = useState({}); // phone → { name, email, contactid }
  // recordingUrls: { contactId: url | null } — null = no recording, undefined = not checked yet
  const [recordingUrls, setRecordingUrls] = useState({});
  const [checkingRecordings, setCheckingRecordings] = useState(false);

  // Pre-fetch recording URLs for all eligible calls after they load
  const checkRecordings = useCallback(async (contacts) => {
    const eligible = contacts.filter(c => !c.abandoned && c.agentSeconds > 0);
    if (!eligible.length) return;
    setCheckingRecordings(true);
    const results = {};
    // Check in batches of 5 to avoid hammering the API
    for (let i = 0; i < eligible.length; i += 5) {
      const batch = eligible.slice(i, i + 5);
      const promises = batch.map(async (c) => {
        try {
          const data = await cxCall('recording', { contactId: String(c.contactId) });
          const url = data.interactions?.[0]?.data?.fileToPlayUrl;
          results[c.contactId] = url || null;
        } catch (_) {
          results[c.contactId] = null;
        }
      });
      await Promise.all(promises);
      setRecordingUrls(prev => ({ ...prev, ...results }));
    }
    setCheckingRecordings(false);
  }, []);

  // Compute API date range from preset or custom inputs
  const getDateRange = useCallback(() => {
    const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local tz
    const dayOffset = (n) => {
      const d = new Date(); d.setDate(d.getDate() + n);
      return d.toLocaleDateString('en-CA');
    };
    switch (preset) {
      case 'today': return { startDate: todayStr, endDate: dayOffset(1) };
      case 'yesterday': return { startDate: dayOffset(-1), endDate: todayStr };
      case '3d': return { startDate: dayOffset(-2), endDate: dayOffset(1) };
      case '7d': return { startDate: dayOffset(-6), endDate: dayOffset(1) };
      case 'custom': {
        if (!customStart || !customEnd) return null;
        // endDate is exclusive in the API, so add 1 day
        const end = new Date(customEnd + 'T12:00:00');
        end.setDate(end.getDate() + 1);
        return { startDate: customStart, endDate: end.toLocaleDateString('en-CA') };
      }
      default: return { startDate: todayStr, endDate: dayOffset(1) };
    }
  }, [preset, customStart, customEnd]);

  const load = useCallback(() => {
    const range = getDateRange();
    if (!range) return;
    setLoading(true);
    setError(null);
    setRecordingUrls({});
    const { startDate, endDate } = range;
    const PAGE = 500;
    // Paginate to get all completed contacts (API caps at `top` per request)
    (async () => {
      let all = [];
      let skip = 0;
      for (;;) {
        const data = await cxCall('completed-contacts', { startDate, endDate, top: PAGE, skip });
        const batch = data.completedContacts || [];
        all = all.concat(batch);
        if (batch.length < PAGE) break; // last page
        skip += PAGE;
      }
      return all;
    })()
      .then(async (allContacts) => {
        const contacts = allContacts;
        contacts.sort((a, b) => new Date(b.contactStartDate) - new Date(a.contactStartDate));
        setCalls(contacts);

        // Look up customers by phone numbers
        const phones = new Set();
        contacts.forEach(c => {
          if (c.fromAddress && c.fromAddress.length >= 7) phones.add(c.fromAddress.replace(/\D/g, ''));
          if (c.toAddress && c.toAddress.length >= 7) phones.add(c.toAddress.replace(/\D/g, ''));
        });
        if (phones.size > 0) {
          try {
            // Filter out known company numbers — they'd match customers who have the company number stored as their phone
            const companyNumbers = new Set(Object.keys(KNOWN_NUMBERS));
            const phoneList = [...phones].filter(p => p.length >= 7 && !companyNumbers.has(p.slice(-10)));
            if (!phoneList.length) throw new Error('no customer phones');
            // Match last 10 digits to handle +1 prefix differences
            const likeClauses = phoneList.slice(0, 50).map(p => {
              const last10 = p.slice(-10);
              return `phone LIKE '%${last10}%' OR shipphone LIKE '%${last10}%'`;
            }).join(' OR ');
            // Also exclude customers whose phone IS a company number (bad data)
            const excludeClauses = [...companyNumbers].map(n => `phone NOT LIKE '%${n}%' AND shipphone NOT LIKE '%${n}%'`).join(' AND ');
            const res = await neonRawSelect(`SELECT contactid, firstname, lastname, email, phone, shipphone,
              EXISTS(SELECT 1 FROM vip_memberships v WHERE v.customer_id = contactid::int AND v.active = true) as is_vip,
              EXISTS(SELECT 1 FROM vip_memberships v WHERE v.customer_id = contactid::int AND v.active = true AND v.auto_renew = false) as vip_not_renewing,
              EXISTS(SELECT 1 FROM celebrity_candidates cc WHERE cc.contactid = customers.contactid AND cc.review_action = 'confirmed') as is_celeb,
              (SELECT STRING_AGG(cc.name || ' (' || COALESCE(cc.category, 'other') || ')', ', ') FROM celebrity_candidates cc WHERE cc.contactid = customers.contactid AND cc.review_action = 'confirmed') as celeb_names
              FROM customers WHERE (${likeClauses}) AND ${excludeClauses} LIMIT 200`);
            const map = {};
            (res.rows || []).forEach(r => {
              const normalize = ph => (ph || '').replace(/\D/g, '').slice(-10);
              const key1 = normalize(r.phone);
              const key2 = normalize(r.shipphone);
              // Skip if their phone is actually a company number
              if (companyNumbers.has(key1) && companyNumbers.has(key2)) return;
              const entry = { name: [r.firstname, r.lastname].filter(Boolean).join(' '), email: r.email, contactid: r.contactid, isVip: !!r.is_vip, vipNotRenewing: !!r.vip_not_renewing, isCeleb: !!r.is_celeb, celebNames: r.celeb_names };
              if (key1 && !companyNumbers.has(key1)) map[key1] = entry;
              if (key2 && !companyNumbers.has(key2)) map[key2] = entry;
            });
            setCustomerMap(map);
          } catch (e) { /* phone lookup failed, not critical */ }
        }
        // Recordings are checked per-page via useEffect
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [getDateRange]);

  // Load on mount + when preset changes (custom mode uses Go button)
  useEffect(() => { if (preset !== 'custom') load(); }, [preset]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  const totalCalls = calls.length;
  const inbound = calls.filter(c => !c.isOutbound).length;
  const outbound = calls.filter(c => c.isOutbound).length;
  const abandoned = calls.filter(c => c.abandoned).length;
  const avgDuration = totalCalls ? Math.round(calls.reduce((s, c) => s + (c.agentSeconds || 0), 0) / totalCalls) : 0;

  const totalPages = Math.ceil(totalCalls / pageSize) || 1;
  const pagedCalls = calls.slice(page * pageSize, (page + 1) * pageSize);

  // Only check recordings for the visible page
  useEffect(() => {
    if (pagedCalls.length) checkRecordings(pagedCalls);
  }, [page, pageSize, calls]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to page 0 when data or page size changes
  useEffect(() => { setPage(0); }, [preset, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12 }}>
        {[
          { label: 'Total Calls', value: totalCalls, color: 'var(--navy)' },
          { label: 'Inbound', value: inbound, color: '#1a8a4a' },
          { label: 'Outbound', value: outbound, color: '#2563eb' },
          { label: 'Abandoned', value: abandoned, color: '#c0392b' },
          { label: 'Avg Duration', value: formatDuration(avgDuration), color: 'var(--orange)' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: '8px 12px' }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><Phone size={14} style={{ marginRight: 6 }} />Contact Center Call Log</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {[
              { key: 'today', label: 'Today' },
              { key: 'yesterday', label: 'Yesterday' },
              { key: '3d', label: '3d' },
              { key: '7d', label: '7d' },
              { key: 'custom', label: 'Custom' },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setPreset(key)} style={{
                fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700,
                background: preset === key ? 'var(--navy)' : 'var(--gray-200)', color: preset === key ? '#fff' : 'var(--gray-600)',
              }}>
                {label}
              </button>
            ))}
            {preset === 'custom' && (
              <>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  style={{ fontSize: 10, padding: '2px 6px', border: '1px solid var(--gray-300)', borderRadius: 4, fontFamily: 'inherit' }} />
                <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>to</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  style={{ fontSize: 10, padding: '2px 6px', border: '1px solid var(--gray-300)', borderRadius: 4, fontFamily: 'inherit' }} />
                <button onClick={load} disabled={!customStart || !customEnd || loading} style={{
                  fontSize: 10, padding: '2px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700,
                  background: 'var(--orange)', color: '#fff', opacity: (!customStart || !customEnd) ? 0.5 : 1,
                }}>Go</button>
              </>
            )}
            <button onClick={load} disabled={loading} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--orange)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, marginLeft: 8 }}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>
        {error && <div style={{ padding: 12, color: '#c0392b', fontSize: 12 }}>{error}</div>}
        {loading && !calls.length ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>Loading calls...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={th}>Date/Time</th>
                  <th style={th}>Agent</th>
                  <th style={th}>Customer</th>
                  <th style={th}>From</th>
                  <th style={th}>To</th>
                  <th style={th}>Skill</th>
                  <th style={th}>Duration</th>
                  <th style={th}>Queue</th>
                  <th style={th}>Result</th>
                  <th style={th}>Recording</th>
                </tr>
              </thead>
              <tbody>
                {pagedCalls.map((c, i) => {
                  const isMissed = c.abandoned || c.endReason === 'Abandoned';
                  const direction = c.isOutbound ? 'Outbound' : 'Inbound';
                  const result = isMissed ? 'Abandoned' : c.endReason || 'Completed';
                  return (
                    <tr key={c.contactId || i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={td}>{formatDateTime(c.contactStartDate)}</td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: 'var(--navy)' }}>
                          {c.firstName && c.lastName ? `${c.firstName} ${c.lastName}` : '—'}
                        </div>
                        <div>{directionBadge(direction, isMissed ? 'Abandoned' : '')}</div>
                      </td>
                      <td style={td}>
                        {(() => {
                          const fromKey = (c.fromAddress || '').replace(/\D/g, '').slice(-10);
                          const toKey = (c.toAddress || '').replace(/\D/g, '').slice(-10);
                          const cust = customerMap[fromKey] || customerMap[toKey];
                          if (!cust) return <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>—</span>;
                          return (
                            <div>
                              <a onClick={() => navigateTo?.('customer-search', null, { email: cust.email })}
                                style={{ fontWeight: 600, color: 'var(--orange)', cursor: 'pointer', borderBottom: '1px dotted var(--orange)', fontSize: 11.5 }}>
                                {cust.name}
                              </a>
                              {cust.isVip && <VipBadge notRenewing={cust.vipNotRenewing} style={{ fontSize: 8, fontWeight: 700, background: 'var(--orange)', color: '#fff', padding: '1px 5px', borderRadius: 3, marginLeft: 5, verticalAlign: 'middle' }} />}
                              {cust.isCeleb && <span title={cust.celebNames || 'Confirmed celebrity'} style={{ fontSize: 8, fontWeight: 700, background: '#8b5cf6', color: '#fff', padding: '1px 5px', borderRadius: 3, marginLeft: 4, verticalAlign: 'middle', cursor: 'help' }}>CELEB</span>}
                              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1 }}>{cust.email}</div>
                            </div>
                          );
                        })()}
                      </td>
                      <td style={td}>
                        {formatPhone(c.fromAddress)}
                        {(() => {
                          const key = (c.fromAddress || '').replace(/\D/g, '').slice(-10);
                          const known = KNOWN_NUMBERS[key];
                          if (known) return <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)' }}>{known}</div>;
                          return null;
                        })()}
                      </td>
                      <td style={td}>
                        {formatPhone(c.toAddress)}
                        {(() => {
                          const key = (c.toAddress || '').replace(/\D/g, '').slice(-10);
                          const known = KNOWN_NUMBERS[key];
                          if (known) return <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)' }}>{known}</div>;
                          return null;
                        })()}
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: '#f0f4ff', color: '#3b5bdb' }}>
                          {c.skillName || '—'}
                        </span>
                      </td>
                      <td style={td}>{formatDuration(c.agentSeconds || c.totalDurationSeconds)}</td>
                      <td style={td}>{formatDuration(c.inQueueSeconds)}</td>
                      <td style={td}>
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                          background: isMissed ? '#fee2e2' : '#dcfce7',
                          color: isMissed ? '#991b1b' : '#166534',
                        }}>
                          {result}
                        </span>
                      </td>
                      <td style={{ ...td, minWidth: 200 }}>
                        {recordingUrls[c.contactId] === null ? (
                          <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>N/A</span>
                        ) : recordingUrls[c.contactId] ? (
                          <RecordingPlayer url={recordingUrls[c.contactId]} />
                        ) : !isMissed && c.agentSeconds > 0 ? (
                          <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>{checkingRecordings ? '...' : ''}</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {!calls.length && !loading && (
                  <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: 'var(--gray-400)' }}>No calls found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {totalCalls > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid var(--gray-200)', fontSize: 11 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gray-500)' }}>
              <span>Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalCalls)} of {totalCalls}</span>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                style={{ fontSize: 11, border: '1px solid var(--gray-300)', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', fontFamily: 'inherit' }}>
                {[25, 50, 100].map(n => <option key={n} value={n}>{n} per page</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setPage(0)} disabled={page === 0}
                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: '1px solid var(--gray-300)', background: '#fff', cursor: page === 0 ? 'default' : 'pointer', color: page === 0 ? 'var(--gray-300)' : 'var(--navy)', fontWeight: 600 }}>
                First
              </button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: '1px solid var(--gray-300)', background: '#fff', cursor: page === 0 ? 'default' : 'pointer', color: page === 0 ? 'var(--gray-300)' : 'var(--navy)', fontWeight: 600 }}>
                Prev
              </button>
              <span style={{ fontSize: 11, padding: '3px 8px', color: 'var(--gray-600)', fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: '1px solid var(--gray-300)', background: '#fff', cursor: page >= totalPages - 1 ? 'default' : 'pointer', color: page >= totalPages - 1 ? 'var(--gray-300)' : 'var(--navy)', fontWeight: 600 }}>
                Next
              </button>
              <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, border: '1px solid var(--gray-300)', background: '#fff', cursor: page >= totalPages - 1 ? 'default' : 'pointer', color: page >= totalPages - 1 ? 'var(--gray-300)' : 'var(--navy)', fontWeight: 600 }}>
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── SMS Tab ── */
function SmsTab() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toNumber, setToNumber] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    rcCall('sms-list', { perPage: 50 }).then(data => setMessages(data.records || []))
      .catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!toNumber || !msgBody) return;
    setSending(true); setSendResult(null);
    try {
      await rcCall('sms-send', { to: toNumber.replace(/\D/g, '').replace(/^(\d{10})$/, '+1$1'), text: msgBody });
      setSendResult({ ok: true, msg: 'SMS sent successfully' });
      setMsgBody(''); load();
    } catch (err) { setSendResult({ ok: false, msg: err.message }); }
    finally { setSending(false); }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header"><Send size={14} style={{ marginRight: 6 }} />Send SMS</div>
        <form onSubmit={handleSend} style={{ padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TO NUMBER</div>
            <input type="text" value={toNumber} onChange={e => setToNumber(e.target.value)} placeholder="(410) 555-1234" className="form-input" style={{ fontSize: 11, width: 150 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>MESSAGE</div>
            <input type="text" value={msgBody} onChange={e => setMsgBody(e.target.value)} placeholder="Type your message..." className="form-input" style={{ fontSize: 11, width: '100%' }} />
          </div>
          <button type="submit" disabled={sending || !toNumber || !msgBody} className="btn btn-primary" style={{ fontSize: 11, padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Send size={12} /> {sending ? 'Sending...' : 'Send'}
          </button>
        </form>
        {sendResult && <div style={{ padding: '0 16px 10px', fontSize: 11, color: sendResult.ok ? '#1a8a4a' : '#c0392b' }}>{sendResult.msg}</div>}
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><MessageSquare size={14} style={{ marginRight: 6 }} />Recent Messages</span>
          <button onClick={load} disabled={loading} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--orange)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
        {error && <div style={{ padding: 12, color: '#c0392b', fontSize: 12 }}>{error}</div>}
        {loading && !messages.length ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>Loading messages...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={th}>Date</th><th style={th}>Direction</th><th style={th}>From</th><th style={th}>To</th><th style={{ ...th, minWidth: 200 }}>Message</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m, i) => (
                  <tr key={m.id || i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={td}>{formatDateTime(m.creationTime || m.lastModifiedTime)}</td>
                    <td style={td}>{directionBadge(m.direction, m.readStatus)}</td>
                    <td style={td}>{formatPhone(m.from?.phoneNumber || '')}</td>
                    <td style={td}>{(m.to || []).map(t => formatPhone(t.phoneNumber || '')).join(', ')}</td>
                    <td style={{ ...td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.subject || ''}</td>
                  </tr>
                ))}
                {!messages.length && !loading && (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--gray-400)' }}>No messages found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Voicemail Tab ── */
function VoicemailTab() {
  const [voicemails, setVoicemails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    rcCall('voicemail-list', { perPage: 50 }).then(data => setVoicemails(data.records || []))
      .catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span><Voicemail size={14} style={{ marginRight: 6 }} />Voicemails</span>
        <button onClick={load} disabled={loading} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--orange)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      {error && <div style={{ padding: 12, color: '#c0392b', fontSize: 12 }}>{error}</div>}
      {loading && !voicemails.length ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>Loading voicemails...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={th}>Date</th><th style={th}>Caller</th><th style={th}>Duration</th><th style={th}>Status</th><th style={th}>Play</th>
              </tr>
            </thead>
            <tbody>
              {voicemails.map((vm, i) => {
                const attachment = (vm.attachments || [])[0];
                return (
                  <tr key={vm.id || i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={td}>{formatDateTime(vm.creationTime)}</td>
                    <td style={td}>{formatPhone(vm.from?.phoneNumber || vm.from?.name || 'Unknown')}</td>
                    <td style={td}>{formatDuration(vm.vmDuration || attachment?.vmDuration)}</td>
                    <td style={td}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: vm.readStatus === 'Read' ? '#f1f5f9' : '#dbeafe', color: vm.readStatus === 'Read' ? '#475569' : '#1e40af' }}>
                        {vm.readStatus || 'Unread'}
                      </span>
                    </td>
                    <td style={td}>
                      {attachment?.uri ? (
                        <button onClick={() => window.open(attachment.uri, '_blank')} style={{ background: 'none', border: '1px solid var(--gray-200)', borderRadius: 4, cursor: 'pointer', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--navy)' }}>
                          <Play size={11} /> Play
                        </button>
                      ) : <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>N/A</span>}
                    </td>
                  </tr>
                );
              })}
              {!voicemails.length && !loading && (
                <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--gray-400)' }}>No voicemails found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const ARCHIVE_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/archive-recordings'
  : '/.netlify/functions/archive-recordings';

async function archiveCall(action, params = {}) {
  const res = await fetch(ARCHIVE_PROXY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, params }) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Archive API error ${res.status}`);
  return data;
}

/* ── Recording Archive Tab ── */
function ArchiveTab() {
  const [recordings, setRecordings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [archiving, setArchiving] = useState(false);
  const [archiveResult, setArchiveResult] = useState(null);
  const pageSize = 50;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { page, pageSize };
    if (query) params.query = query;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    archiveCall('search', params)
      .then(data => {
        setRecordings(data.recordings || []);
        setTotal(data.total || 0);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [query, startDate, endDate, page]);

  useEffect(() => { load(); }, [load]);

  const runArchive = async () => {
    setArchiving(true);
    setArchiveResult(null);
    try {
      const result = await archiveCall('archive', { days: 30 });
      setArchiveResult(result);
      load(); // Refresh list
    } catch (e) {
      setArchiveResult({ error: e.message });
    }
    setArchiving(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(searchInput);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      {/* Archive controls */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><Archive size={14} style={{ marginRight: 6 }} />Recording Archive</span>
          <button onClick={runArchive} disabled={archiving} className="btn btn-primary" style={{ fontSize: 11, padding: '5px 14px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Download size={12} /> {archiving ? 'Archiving...' : 'Archive Now (30 days)'}
          </button>
        </div>
        {archiveResult && (
          <div style={{ padding: '8px 16px', fontSize: 11, color: archiveResult.error ? 'var(--red)' : 'var(--green)', background: archiveResult.error ? '#fee2e2' : '#dcfce7', borderBottom: '1px solid var(--gray-200)' }}>
            {archiveResult.error
              ? `Error: ${archiveResult.error}`
              : `Archived ${archiveResult.archived} recordings, ${archiveResult.skipped} already saved, ${archiveResult.errors} errors (${archiveResult.dateRange})`}
          </div>
        )}
        <form onSubmit={handleSearch} style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>SEARCH</div>
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Agent, phone number..." className="form-input" style={{ fontSize: 11, width: 180 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>FROM</div>
            <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TO</div>
            <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <button type="submit" className="btn btn-primary" style={{ fontSize: 11, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Search size={12} /> Search
          </button>
          {(query || startDate || endDate) && (
            <button type="button" onClick={() => { setSearchInput(''); setQuery(''); setStartDate(''); setEndDate(''); setPage(1); }} style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 11, padding: '6px 10px', cursor: 'pointer', color: 'var(--gray-700)' }}>
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{total} archived recording{total !== 1 ? 's' : ''}</span>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 11 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ border: '1px solid var(--gray-300)', background: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>Prev</button>
              <span style={{ color: 'var(--gray-500)' }}>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ border: '1px solid var(--gray-300)', background: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>Next</button>
            </div>
          )}
        </div>
        {error && <div style={{ padding: 12, color: '#c0392b', fontSize: 12 }}>{error}</div>}
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>Loading...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={th}>Date</th>
                  <th style={th}>Direction</th>
                  <th style={th}>Agent</th>
                  <th style={th}>From</th>
                  <th style={th}>To</th>
                  <th style={th}>Skill</th>
                  <th style={th}>Duration</th>
                  <th style={th}>Recording</th>
                </tr>
              </thead>
              <tbody>
                {recordings.map((r, i) => (
                  <tr key={r.contact_id} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                    <td style={td}>{formatDateTime(r.contact_date)}</td>
                    <td style={td}>{directionBadge(r.is_outbound ? 'Outbound' : 'Inbound', '')}</td>
                    <td style={{ ...td, fontWeight: 600, color: 'var(--navy)' }}>{r.agent_name || '—'}</td>
                    <td style={td}>{formatPhone(r.from_number)}</td>
                    <td style={td}>{formatPhone(r.to_number)}</td>
                    <td style={td}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: '#f0f4ff', color: '#3b5bdb' }}>
                        {r.skill_name || '—'}
                      </span>
                    </td>
                    <td style={td}>{formatDuration(r.duration_seconds)}</td>
                    <td style={{ ...td, minWidth: 200 }}>
                      <ArchivedRecordingPlayer r2Key={r.r2_key} />
                    </td>
                  </tr>
                ))}
                {!recordings.length && !loading && (
                  <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--gray-400)' }}>
                    {total === 0 ? 'No archived recordings yet. Click "Archive Now" to save recordings from the last 30 days.' : 'No results match your search.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Archived Recording Player (streams from R2) ── */
function ArchivedRecordingPlayer({ r2Key }) {
  const [state, setState] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = React.useRef(null);
  const blobUrlRef = React.useRef(null);

  const loadAudio = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const res = await fetch(ARCHIVE_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'stream', params: { r2Key } }),
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      blobUrlRef.current = blobUrl;
      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      audio.addEventListener('loadedmetadata', () => { setDuration(audio.duration); setState('ready'); audio.play(); setState('playing'); });
      audio.addEventListener('timeupdate', () => { setCurrentTime(audio.currentTime); setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0); });
      audio.addEventListener('ended', () => setState('ready'));
      audio.addEventListener('error', () => setState('error'));
      audio.load();
    } catch { setState('error'); }
  };

  const togglePlay = () => {
    if (!audioRef.current) return loadAudio();
    if (state === 'playing') { audioRef.current.pause(); setState('ready'); }
    else { audioRef.current.play(); setState('playing'); }
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * duration;
  };

  React.useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const fmtTime = (s) => { if (!s || !isFinite(s)) return '0:00'; const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${sec.toString().padStart(2, '0')}`; };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 180 }}>
      <button onClick={togglePlay} disabled={state === 'loading'} style={{
        background: state === 'playing' ? 'var(--orange)' : 'var(--navy)', border: 'none', borderRadius: '50%',
        width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: state === 'loading' ? 'wait' : 'pointer', flexShrink: 0, opacity: state === 'loading' ? 0.5 : 1,
      }}>
        {state === 'loading' ? <RefreshCw size={11} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
          : state === 'playing' ? <Pause size={11} color="#fff" />
          : <Play size={11} color="#fff" style={{ marginLeft: 1 }} />}
      </button>
      {(state === 'ready' || state === 'playing') && duration > 0 ? (
        <>
          <div onClick={seek} style={{ flex: 1, height: 6, background: 'var(--gray-200)', borderRadius: 3, cursor: 'pointer', position: 'relative', minWidth: 80 }}>
            <div style={{ width: `${progress}%`, height: '100%', background: state === 'playing' ? 'var(--orange)' : 'var(--navy)', borderRadius: 3, transition: 'width 0.1s linear' }} />
          </div>
          <span style={{ fontSize: 9, color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{fmtTime(currentTime)}/{fmtTime(duration)}</span>
        </>
      ) : state === 'error' ? <span style={{ fontSize: 10, color: 'var(--red)' }}>Error</span>
        : state === 'idle' ? <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>Play</span>
        : null}
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' };
const td = { padding: '8px 10px', fontSize: 12 };

const TABS = [
  { id: 'calls', label: 'Call Log', icon: Phone },
  { id: 'sms', label: 'SMS', icon: MessageSquare },
  { id: 'voicemail', label: 'Voicemail', icon: Voicemail },
  { id: 'archive', label: 'Recording Archive', icon: Archive },
];

export default function RingCentral({ navigateTo, screenPopEnabled: screenPopProp, onScreenPopToggle }) {
  const [tab, setTab] = useState('calls');
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const screenPopEnabled = screenPopProp ?? (localStorage.getItem('screenPopEnabled') !== 'false');

  const toggleScreenPop = async () => {
    const next = !screenPopEnabled;
    if (onScreenPopToggle) onScreenPopToggle(next);
    localStorage.setItem('screenPopEnabled', String(next));
    setSaveState('saving');
    try {
      const user = getCurrentUser();
      if (user?.id) await neonQuery('user_pref_set', { user_id: user.id, key: 'screenPopEnabled', value: String(next) });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1500);
    } catch (e) {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 2500);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
          RingCentral
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500)', marginLeft: 8 }}>Contact Center + MVP</span>
        </h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--gray-600)', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={screenPopEnabled} onChange={toggleScreenPop} style={{ accentColor: 'var(--orange)' }} />
          Show incoming call popup
          {saveState === 'saving' && <span style={{ fontSize: 11, color: 'var(--gray-500)', marginLeft: 4 }}>Saving…</span>}
          {saveState === 'saved' && <span style={{ fontSize: 11, color: '#0a7a2a', marginLeft: 4, fontWeight: 600 }}>Saved ✓</span>}
          {saveState === 'error' && <span style={{ fontSize: 11, color: '#c42a2a', marginLeft: 4, fontWeight: 600 }}>Save failed</span>}
        </label>
      </div>

      {/* Future-step reminder — see CLAUDE.md "Future: train Jimmy from call recordings" */}
      <div style={{
        marginBottom: 16,
        padding: '10px 14px',
        background: '#FFF8E5',
        border: '1px solid #F4D06F',
        borderRadius: 8,
        fontSize: 12,
        color: '#6B4F00',
        lineHeight: 1.5,
      }}>
        <strong>Future idea — teach Jimmy from these calls.</strong> Summarize recordings into
        <code style={{ background: '#FCEFB4', padding: '0 4px', borderRadius: 3, margin: '0 3px' }}>jimmy_knowledge</code>
        entries (PII-redacted, human-reviewed) so Jimmy absorbs CS answers over time. Scope + tradeoffs are in
        the cpx-prototype <code style={{ background: '#FCEFB4', padding: '0 4px', borderRadius: 3, margin: '0 3px' }}>CLAUDE.md</code>
        under "Future: Jimmy learning pipeline."
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid var(--gray-200)' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '8px 20px', fontSize: 12, fontWeight: active ? 700 : 500,
              color: active ? 'var(--orange)' : 'var(--gray-500)',
              background: 'none', border: 'none', borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'calls' && <CallLogTab navigateTo={navigateTo} />}
      {tab === 'sms' && <SmsTab />}
      {tab === 'voicemail' && <VoicemailTab />}
      {tab === 'archive' && <ArchiveTab />}
    </div>
  );
}
